/**
 * Retorno do checkout público (`/pay/:token`).
 *
 * O checkout roda fora do layout do sistema (rota isolada em `routes.tsx`), então
 * quem chega lá pagando uma mensalidade, um pacote ou uma cobrança do portal fica
 * sem menu, sem histórico útil e — depois do "Pagamento Confirmado" — sem saída.
 *
 * A tela de sucesso já sabe voltar: basta receber `return_url` (para onde voltar) e
 * `from` (rótulo do botão). Este helper carimba os dois no link, preservando os
 * parâmetros que o backend já tenha colocado nele.
 */

/** Origem do checkout; define o rótulo do botão de retorno. */
export type CheckoutOrigin = 'app' | 'flex' | 'store'

/**
 * Acrescenta `from` e `return_url` a um link de checkout.
 *
 * @param payUrl Link do checkout (absoluto, vindo do backend, ou relativo `/pay/:token`).
 * @param origin Origem, para o rótulo do botão de retorno. Padrão: `app` (dentro do sistema).
 * @param returnUrl Endereço de volta. Padrão: a página atual.
 */
export function withCheckoutReturn(
    payUrl: string,
    origin: CheckoutOrigin = 'app',
    returnUrl?: string,
): string {
    if (typeof window === 'undefined') return payUrl

    const target = returnUrl ?? window.location.href
    // `URL` exige base para links relativos; a origem atual é sempre a certa aqui.
    const url = new URL(payUrl, window.location.origin)

    if (!url.searchParams.has('from')) url.searchParams.set('from', origin)
    if (!url.searchParams.has('return_url')) {
        url.searchParams.set('return_url', target)
    }

    return url.toString()
}
